import { nanoid } from 'nanoid';
import {
  useRef,
  useState,
  type RefObject,
  type DragEventHandler,
  type ChangeEventHandler,
} from 'react';

import { wrapArray } from '~/utils';
import { uploaderApi } from '~/requests/uploaderApi';

import type { IFileProps, TAccept } from '~/types';

interface IUseUploadParams {
  accept: TAccept[];
  maximumSize?: number;
  onUploadComplete?: (file: IFileProps) => void;
}

interface IUseSingleUploadParams extends IUseUploadParams {
  isMulti: false;
  value?: IFileProps;
  onChange?: (file: IFileProps) => void;
}

interface IUseMultiUploadParams extends IUseUploadParams {
  isMulti: true;
  value?: IFileProps | IFileProps[];
  onChange?: (file: IFileProps | IFileProps[]) => void;
}

interface IUseUploadReturnProps {
  files: IFileProps[];
  isDragging: boolean;
  remove: (file: IFileProps) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onDragEnter: DragEventHandler<HTMLInputElement>;
  onDragLeave: DragEventHandler<HTMLInputElement>;
}

export function useUpload({
  value,
  accept,
  isMulti,
  onChange,
  maximumSize,
  onUploadComplete,
}: IUseSingleUploadParams | IUseMultiUploadParams): IUseUploadReturnProps {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<IFileProps[]>(
    wrapArray(value).map((file) => {
      return {
        ...file,
        id: file.id ?? file.signedId,
      };
    }),
  );
  const [isDragging, setIsDragging] = useState(false);

  const changeFiles = (files: IFileProps[]) => {
    if (onChange) {
      if (isMulti) {
        onChange(
          files.reduce<IFileProps[]>((result, file) => {
            if (file.signedId) {
              result.push(file);
            }

            return result;
          }, []),
        );
      } else {
        onChange(files[0]);
      }
    }
  };

  const handleDraggedIn = () => {
    setIsDragging(true);
  };

  const handleDraggedOut = () => {
    setIsDragging(false);
  };

  const handleRemove = (file: IFileProps) => {
    const newFiles = files.filter((e) => e.id !== file.id);

    setFiles(newFiles);
    changeFiles(newFiles);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    let newFiles = Array.from(event.target.files || []) as IFileProps[];

    if (accept.length) {
      newFiles = newFiles.filter((e) => {
        return accept.includes(e.type);
      });
    }

    if (maximumSize) {
      newFiles = newFiles.filter((e) => {
        return e.size <= maximumSize;
      });
    }

    if (!newFiles.length) {
      return;
    }

    newFiles = newFiles.map<IFileProps>((file) => {
      return Object.assign(file, {
        done: false,
        progress: 0,
        id: nanoid(),
      });
    });

    let updatedFiles = newFiles;

    if (isMulti) {
      updatedFiles = [...files, ...updatedFiles];
    }

    setFiles(updatedFiles);

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    newFiles.forEach((file) => {
      uploaderApi
        .upload({
          file,
          onUploadProgress: ({ loaded, total }) => {
            const percent = (loaded * 100) / (total || loaded);

            setFiles((state) => {
              const f = state.find((e) => e.id === file.id);

              if (f) {
                f.progress = percent;
              }

              return [...state];
            });
          },
        })
        .then((value) => {
          Object.assign(file, {
            signedId: value.signedId,
          });

          setFiles((state) => {
            const f = state.find((e) => e.id === file.id);

            if (f) {
              Object.assign(f, {
                ...value,
                done: true,
              });
            }

            return [...state];
          });

          onUploadComplete?.(file);
          // TODO: make this one better
          changeFiles(updatedFiles);
        });
    });
  };

  return {
    files,
    inputRef,
    isDragging,
    remove: handleRemove,
    onChange: handleChange,
    onDragEnter: handleDraggedIn,
    onDragLeave: handleDraggedOut,
  };
}
